import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const STRATEGIC_OPTIONS = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'standard', label: 'Standard' },
  { value: 'sunset', label: 'Sunset' },
];

const CATEGORY_COLORS = {
  design: 'bg-violet-500/10 text-violet-600',
  supply: 'bg-blue-500/10 text-blue-600',
  installation: 'bg-cyan-500/10 text-cyan-600',
  commissioning: 'bg-teal-500/10 text-teal-600',
  support: 'bg-amber-500/10 text-amber-600',
  'managed-services': 'bg-indigo-500/10 text-indigo-600',
  consulting: 'bg-pink-500/10 text-pink-600',
  training: 'bg-green-500/10 text-green-600',
};

const RISK_COLORS = {
  low: 'bg-success/10 text-success',
  medium: 'bg-warning/10 text-warning',
  high: 'bg-error/10 text-error',
};

const STRATEGIC_COLORS = {
  strategic: 'bg-success/10 text-success border-success',
  standard: 'bg-primary/10 text-primary border-primary',
  sunset: 'bg-warning/10 text-warning border-warning',
};

const EMPTY_FORM = {
  catalogServiceId: '',
  coverageArea: '',
  slaLevels: '',
  strategicMarking: 'standard',
};

const ServicesTable = ({ services, catalogServices = [], onAddService, onEditService, onDeleteService }) => {
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Build catalog options for dropdown
  const catalogOptions = catalogServices?.map(s => ({
    value: s?.id,
    label: `${s?.serviceName}${s?.serviceCategory ? ` — ${s?.serviceCategory}` : ''}`,
  }));

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'catalogServiceId' && value) {
        const catalogItem = catalogServices?.find(s => s?.id === value);
        if (catalogItem) {
          updated.slaLevels = catalogItem?.slaLevels || '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!formData?.catalogServiceId && !editingId) return;
    const catalogItem = catalogServices?.find(s => s?.id === formData?.catalogServiceId);
    const payload = {
      serviceName: catalogItem?.serviceName || formData?.serviceName,
      serviceCategory: catalogItem?.serviceCategory || formData?.serviceCategory,
      deliveryModel: catalogItem?.deliveryModel || formData?.deliveryModel,
      certificationsRequired: catalogItem?.certificationsRequired || formData?.certificationsRequired,
      industryAlignment: catalogItem?.industryAlignment || formData?.industryAlignment,
      coverageArea: formData?.coverageArea,
      slaLevels: formData?.slaLevels || catalogItem?.slaLevels,
      strategicMarking: formData?.strategicMarking,
      catalogServiceId: formData?.catalogServiceId,
    };
    if (editingId) {
      onEditService(editingId, payload);
      setEditingId(null);
    } else {
      onAddService(payload);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setIsAddFormOpen(false);
    setEditingId(null);
  };

  const handleEdit = (service) => {
    setFormData({
      catalogServiceId: service?.catalogServiceId || '',
      coverageArea: service?.coverageArea || '',
      slaLevels: service?.slaLevels || '',
      strategicMarking: service?.strategicMarking || 'standard',
    });
    setEditingId(service?.id);
    setIsAddFormOpen(true);
  };

  const getCategoryColor = (cat) => CATEGORY_COLORS?.[cat] || 'bg-muted text-muted-foreground';
  const getRiskColor = (risk) => RISK_COLORS?.[risk] || 'bg-muted text-muted-foreground';
  const getStrategicColor = (marking) => STRATEGIC_COLORS?.[marking] || 'bg-muted text-muted-foreground border-border';

  return (
    <div className="space-y-16 p-16">
      {/* Catalog notice banner */}
      <div className="flex items-start gap-10 p-12 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-700">
        <Icon name="Info" size={16} className="shrink-0 mt-1" />
        <span>Services are selected from the <strong>Master Services Catalog</strong> — no free-text entry. This ensures consistent naming across all partners.</span>
      </div>
      <div className="flex items-center justify-between gap-12 flex-wrap">
        <Button
          variant={isAddFormOpen ? 'outline' : 'default'}
          iconName={isAddFormOpen ? 'X' : 'Plus'}
          iconPosition="left"
          iconSize={18}
          onClick={() => { setIsAddFormOpen(!isAddFormOpen); if (isAddFormOpen) resetForm(); }}
        >
          {isAddFormOpen ? 'Cancel' : 'Add Service from Catalog'}
        </Button>
      </div>
      {isAddFormOpen && (
        <div className="bg-card border border-border rounded-lg p-16 md:p-24 shadow-elevation-sm">
          <h3 className="text-lg font-semibold text-foreground mb-16">
            {editingId ? 'Edit Service Mapping' : 'Add Service from Catalog'}
          </h3>

          {catalogOptions?.length === 0 ? (
            <div className="flex items-center gap-10 p-12 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-16">
              <Icon name="AlertTriangle" size={16} />
              <span>No services in the master catalog yet. Ask an admin to add services to the catalog first.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-3">
                <Select
                  label="Select Service from Catalog *"
                  options={catalogOptions}
                  value={formData?.catalogServiceId}
                  onChange={(value) => handleInputChange('catalogServiceId', value)}
                  placeholder="Search and select a service..."
                  required
                />
                {formData?.catalogServiceId && (() => {
                  const item = catalogServices?.find(s => s?.id === formData?.catalogServiceId);
                  return item ? (
                    <div className="mt-8 p-10 bg-slate-50 rounded-lg text-xs text-slate-600 flex flex-wrap gap-12">
                      {item?.serviceCategory && <span><strong>Category:</strong> {item?.serviceCategory}</span>}
                      {item?.deliveryModel && <span><strong>Delivery:</strong> {item?.deliveryModel}</span>}
                      {item?.certificationsRequired && <span><strong>Certs:</strong> {item?.certificationsRequired}</span>}
                      {item?.industryAlignment && <span><strong>Industry:</strong> {item?.industryAlignment}</span>}
                    </div>
                  ) : null;
                })()}
              </div>
              <Input
                label="Coverage Area (Partner)"
                type="text"
                placeholder="e.g., UAE, GCC, MENA"
                value={formData?.coverageArea}
                onChange={(e) => handleInputChange('coverageArea', e?.target?.value)}
              />
              <Input
                label="SLA Commitment (Partner)"
                type="text"
                placeholder="e.g., 99.9% uptime"
                value={formData?.slaLevels}
                onChange={(e) => handleInputChange('slaLevels', e?.target?.value)}
              />
              <Select
                label="Strategic Marking"
                options={STRATEGIC_OPTIONS}
                value={formData?.strategicMarking}
                onChange={(value) => handleInputChange('strategicMarking', value)}
              />
            </div>
          )}

          <div className="flex items-center gap-12 mt-16">
            <Button variant="default" onClick={handleSubmit} disabled={!formData?.catalogServiceId && !editingId}>
              {editingId ? 'Update Service' : 'Add Service'}
            </Button>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden shadow-elevation-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Service Name</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Category</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Coverage Area</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">SLA Levels</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Delivery Model</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">AI Risk</th>
                <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Strategic</th>
                <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {services?.map((service) => (
                <tr key={service?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-16 py-12 text-sm text-foreground font-medium">
                    <div>
                      <span>{service?.serviceName}</span>
                      {service?.catalogServiceId && (
                        <span className="ml-6 inline-flex items-center gap-4 text-xs text-teal-500">
                          <Icon name="Link" size={10} />
                          Catalog
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-16 py-12">
                    <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-md text-xs font-medium ${getCategoryColor(service?.serviceCategory)}`}>
                      {service?.serviceCategory}
                    </span>
                  </td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">{service?.coverageArea}</td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">{service?.slaLevels}</td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">{service?.deliveryModel}</td>
                  <td className="px-16 py-12">
                    <div className="flex items-center gap-8">
                      <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${getRiskColor(service?.aiRiskRating)}`}>
                        {service?.aiRiskRating?.toUpperCase() || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-16 py-12">
                    <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium border ${getStrategicColor(service?.strategicMarking)}`}>
                      {STRATEGIC_OPTIONS?.find(s => s?.value === service?.strategicMarking)?.label || service?.strategicMarking}
                    </span>
                  </td>
                  <td className="px-16 py-12">
                    <div className="flex items-center justify-end gap-8">
                      <Button variant="ghost" size="icon" iconName="Edit" iconSize={16} onClick={() => handleEdit(service)} />
                      <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => onDeleteService(service?.id)} />
                    </div>
                  </td>
                </tr>
              ))}
              {(!services || services?.length === 0) && (
                <tr>
                  <td colSpan={8} className="px-16 py-32 text-center text-sm text-muted-foreground">
                    No services mapped yet. Add services from the catalog above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile cards */}
      <div className="lg:hidden space-y-12">
        {services?.map((service) => (
          <div key={service?.id} className="bg-card border border-border rounded-lg p-16 shadow-elevation-sm">
            <div className="flex items-start justify-between gap-12 mb-12">
              <div>
                <p className="font-semibold text-foreground text-sm">{service?.serviceName}</p>
                <p className="text-xs text-muted-foreground mt-2">{service?.serviceCategory} · {service?.coverageArea}</p>
              </div>
              <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium border ${getStrategicColor(service?.strategicMarking)}`}>
                {service?.strategicMarking}
              </span>
            </div>
            <div className="flex items-center gap-8 mt-12">
              <Button variant="outline" size="sm" iconName="Edit" iconSize={14} onClick={() => handleEdit(service)}>Edit</Button>
              <Button variant="ghost" size="sm" iconName="Trash2" iconSize={14} onClick={() => onDeleteService(service?.id)}>Remove</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesTable;